import { useState, useEffect, useCallback } from 'react';
import { apiClient, useAuth } from '../contexts/AuthContext';
import { BottomNav } from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Shield, Search } from 'lucide-react';
import { toast } from 'sonner';
import { AdminStatsGrid } from '../components/admin/AdminStatsGrid';
import { MemberRow } from '../components/admin/MemberRow';
import { EditMemberDialog, EditMetricsDialog, DeleteConfirmDialog, CreateMemberDialog } from '../components/admin/AdminDialogs';
import { MemberHistoryDialog } from '../components/admin/MemberHistoryDialog';

export default function AdminPage() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [editMember, setEditMember] = useState(null);
  const [editMetrics, setEditMetrics] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newMember, setNewMember] = useState({ name: '', role: 'user' });
  const [historyMember, setHistoryMember] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [membersRes, statsRes] = await Promise.all([
        apiClient.get('/admin/members'),
        apiClient.get('/admin/stats')
      ]);
      setMembers(membersRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 403) toast.error('Acces reserve au coach');
      else toast.error('Erreur chargement');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpdateMember = async () => {
    if (!editMember) return;
    try {
      await apiClient.put(`/admin/members/${editMember.id}`, { name: editMember.name, role: editMember.role, points: editMember.points });
      toast.success('Membre mis a jour');
      setEditMember(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleUpdateMetrics = async () => {
    if (!editMetrics) return;
    try {
      await apiClient.put(`/admin/members/${editMetrics.id}/metrics`, {
        weight: editMetrics.weight ? parseFloat(editMetrics.weight) : undefined,
        body_fat: editMetrics.body_fat ? parseFloat(editMetrics.body_fat) : undefined,
        muscle_mass: editMetrics.muscle_mass ? parseFloat(editMetrics.muscle_mass) : undefined,
        metabolic_age: editMetrics.metabolic_age ? parseInt(editMetrics.metabolic_age) : undefined,
        timestamp: editMetrics.timestamp ? new Date(editMetrics.timestamp).toISOString() : undefined
      });
      toast.success('Metriques enregistrees');
      setEditMetrics(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleCreateMember = async () => {
    if (!newMember.name.trim()) { toast.error('Le nom est requis'); return; }
    try {
      await apiClient.post('/admin/members', { name: newMember.name.trim(), role: newMember.role });
      toast.success(`${newMember.name} cree !`);
      setNewMember({ name: '', role: 'user' });
      setShowCreate(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (id) => {
    try {
      const { data } = await apiClient.delete(`/admin/members/${id}`);
      toast.success(data.message);
      setDeleteConfirm(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center p-4">
        <div className="text-center">
          <Shield className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <p className="text-zinc-400 text-lg">Acces reserve au coach</p>
        </div>
        <BottomNav />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const filtered = members.filter(m => m.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="min-h-screen bg-[#0F0F0F] pb-20">
      <header className="sticky top-0 z-40 bg-[#0F0F0F]/95 backdrop-blur-sm border-b border-[#2A2A2E]">
        <div className="px-4 py-3">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#FF6B35]" />
            <h1 className="text-xl font-bold text-white">Admin Panel</h1>
          </div>
          <p className="text-xs text-zinc-500">Gestion des membres et donnees</p>
        </div>
      </header>

      <main className="p-4 space-y-4 animate-fade-in">
        <AdminStatsGrid stats={stats} />

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un membre..." className="bg-[#1B1B1B] border-[#2A2A2E] text-white pl-9" data-testid="admin-search" />
        </div>

        <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm" data-testid="admin-members">
          <div className="p-3 border-b border-[#2A2A2E] flex items-center justify-between">
            <span className="text-sm text-zinc-400">{filtered.length} membre{filtered.length > 1 ? 's' : ''}</span>
            <Button size="sm" className="bg-[#FF6B35] text-white hover:bg-[#FF8C55]" onClick={() => setShowCreate(true)} data-testid="create-member-btn">
              + Nouveau profil
            </Button>
          </div>
          <div className="divide-y divide-zinc-800">
            {filtered.map(member => (
              <MemberRow
                key={member.id}
                member={member}
                isExpanded={expandedId === member.id}
                onToggle={() => setExpandedId(expandedId === member.id ? null : member.id)}
                onEditMember={() => setEditMember({ id: member.id, name: member.name, role: member.role, points: member.points, tier: member.tier || '' })}
                onEditMetrics={() => setEditMetrics({
                  id: member.id, name: member.name,
                  weight: member.metrics?.weight || '', body_fat: member.metrics?.body_fat || '',
                  muscle_mass: member.metrics?.muscle_mass || '', metabolic_age: member.metrics?.metabolic_age || ''
                })}
                onDelete={() => setDeleteConfirm({ id: member.id, name: member.name })}
                onViewHistory={() => setHistoryMember({ id: member.id, name: member.name })}
                onRefresh={fetchData}
              />
            ))}
          </div>
        </div>
      </main>

      <EditMemberDialog editMember={editMember} setEditMember={setEditMember} onSave={handleUpdateMember} />
      <EditMetricsDialog editMetrics={editMetrics} setEditMetrics={setEditMetrics} onSave={handleUpdateMetrics} />
      <DeleteConfirmDialog deleteConfirm={deleteConfirm} setDeleteConfirm={setDeleteConfirm} onConfirm={handleDelete} />
      <CreateMemberDialog showCreate={showCreate} setShowCreate={setShowCreate} newMember={newMember} setNewMember={setNewMember} onCreate={handleCreateMember} />
      <MemberHistoryDialog memberId={historyMember?.id} memberName={historyMember?.name} open={!!historyMember} onClose={() => setHistoryMember(null)} />

      <BottomNav />
    </div>
  );
}
